import isEqual from 'lodash/isEqual';
import { bool, func } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, FormGroup, Input, Label } from 'reactstrap';

import Association from '../../../associations/Association';
import { getSuggestedOptions } from '../../../associations/utils';
import { CampaignLink } from '../../../campaigns/Link';
import DateRange from '../../../generic/DateRange';
import Loading from '../../../generic/Loading';
import MandatoryTextForFields from '../../../generic/MandatoryTextForFields';
import SingleSelect from '../../../generic/SingleSelect';
import TagInput from '../../../generic/TagInput';
import getInfoTooltip from '../../../generic/Tooltip/get-info-tooltip';
import { isPC } from '../../../layout/Variant/Variant';
import { opportunityShape } from '../../../prop-types';
import {
  brandCodeFieldText,
  businessModels,
  campaignTypeOptions,
  selfServiceCampaignStatusesOptions,
  useCasesOptions,
} from '../../constants';
import DefaultCopyAssetsWarning from '../../DefaultCopyAssetsWarning';
import HELP_TEXTS from '../../help-texts';
import { suggestionsCall } from '../../utils/utils';
import OpportunityCampaignObjective from '../OpportunityCampaignObjective';

function OpportunityDetails({
  model,
  embeddedInAgreement,
  readOnly,
  onChange,
  onSubmit,
  isSalesChannelSelfService,
  hasPcLeadRole,
  isSameSalesUnitAsOwner,
  isSalesforceOpp,
}) {
  const {
    accountSfId,
    name,
    campaignId,
    campaignEnd,
    campaignType,
    campaignStart,
    commercialPackageId,
    useCase,
    brandCodes,
    selfServiceCampaignStatus,
    targetSupplierIds,
    merchantIds,
    businessModel,
  } = model;
  const [brandIds, setBrandIds] = useState(model.brandIds);
  const [suggestedMerchants, setSuggestedMerchants] = useState([]);
  const [suggestedTargetSuppliers, setSuggestedTargetSuppliers] = useState([]);

  useEffect(() => {
    setBrandIds(model.brandIds);
    if (brandIds && businessModel) {
      suggestionsCall(brandIds, businessModel)
        .then(result => {
          getSuggestedOptions('zmsamMerchant', result.merchantIds)
            .then(suggestions => setSuggestedMerchants(suggestions))
            .catch(console.error); // eslint-disable-line no-console

          getSuggestedOptions('targetSuppliers', result.targetSupplierIds)
            .then(suggestions => setSuggestedTargetSuppliers(suggestions))
            .catch(console.error); // eslint-disable-line no-console
        })
        .catch(() => {});
    }
  }, [model.brandIds, brandIds, businessModel]);

  const brandsHaveChanged = !isEqual(brandIds, model.brandIds);
  const inputProps = { onChange, readOnly };
  const areMerchantsLoaded = !merchantIds?.includes(undefined);

  const renderCampaignLink = idCampaign => {
    if (typeof idCampaign === 'undefined') {
      return null;
    }
    return (
      <FormGroup row>
        <Col className="col-form-label" sm={4}>
          Campaign
        </Col>
        <Col sm={8}>
          <span className="form-control-plaintext-without-color-override">
            {idCampaign ? (
              <CampaignLink id={idCampaign} />
            ) : (
              'A campaign for this opportunity has not been created yet'
            )}
          </span>
        </Col>
      </FormGroup>
    );
  };
  return (
    <>
      <div className="mb-5">
        <FormGroup row>
          <Label sm={4} for="op-name" className="required">
            Name
          </Label>
          <Col sm={8}>
            <Input
              id="op-name"
              name="name"
              value={name || ''}
              {...inputProps}
              readOnly={readOnly || isSalesforceOpp}
            />
          </Col>
        </FormGroup>
        {!embeddedInAgreement && (
          <FormGroup row>
            <Label sm={4} for="op-accountSfId" className="required">
              Account ID
            </Label>
            <Col sm={8}>
              <Input
                id="op-accountSfId"
                name="accountSfId"
                value={accountSfId || ''}
                {...inputProps}
                readOnly={readOnly || isSalesforceOpp}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={4} for="op-businessModel" className="required">
            Business Model
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={businessModels}
              id="op-businessModel"
              name="businessModel"
              value={businessModel}
              {...inputProps}
              readOnly={readOnly}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-brandIds" className="required">
            Brands {getInfoTooltip(HELP_TEXTS, 'primaryBrand', 'info')}
          </Label>
          <Col sm={8}>
            <Association
              id="op-brandIds"
              name="brandIds"
              value={brandIds}
              type="brand"
              isMulti
              verbose
              {...inputProps}
              readOnly={readOnly || isSalesforceOpp}
            />
          </Col>
          {campaignId && (
            <DefaultCopyAssetsWarning
              campaignId={campaignId}
              onSubmit={onSubmit}
              brandIds={brandIds}
              brandsHaveChanged={brandsHaveChanged}
            />
          )}
        </FormGroup>
        {isPC() && onSubmit && (
          <FormGroup row>
            <Label sm={4} for="op-brandCodes">
              Brands
              {getInfoTooltip(HELP_TEXTS, 'primaryBrand', 'info')}
              <MandatoryTextForFields text={brandCodeFieldText} />
            </Label>
            <Col sm={8}>
              <TagInput
                id="op-brandCodes"
                name="brandCodes"
                value={brandCodes || []}
                {...inputProps}
                readOnly={readOnly || isSalesforceOpp}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={4} for="op-targetSupplierIds">
            Target Suppliers
          </Label>
          <Col sm={8}>
            <Association
              id="op-targetSupplierIds"
              name="targetSupplierIds"
              value={targetSupplierIds}
              type="targetSuppliers"
              suggestions={suggestedTargetSuppliers}
              isMulti
              verbose
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-merchantIds">
            Merchants {getInfoTooltip(HELP_TEXTS, 'merchants', 'info')}
          </Label>
          <Col sm={8}>
            {areMerchantsLoaded ? (
              <Association
                id="op-merchantIds"
                name="merchantIds"
                value={merchantIds}
                type="zmsamMerchant"
                suggestions={suggestedMerchants}
                {...inputProps}
                isMulti
              />
            ) : (
              <Loading className="p-0" />
            )}
          </Col>
        </FormGroup>
        {/* )} */}
      </div>
      <div className="mb-5">
        {!embeddedInAgreement && renderCampaignLink(campaignId)}
        <FormGroup row>
          <Label
            sm={4}
            for="op-campaignDates-campaignStart"
            className="required"
          >
            Campaign Start
          </Label>
          <Col sm={8}>
            <DateRange
              id="op-campaignDates"
              startName="campaignStart"
              endName="campaignEnd"
              startDate={campaignStart || null}
              endDate={campaignEnd || null}
              small
              {...inputProps}
              readOnly={readOnly || isSalesforceOpp}
            />
          </Col>
        </FormGroup>
        {isSalesChannelSelfService && (
          <FormGroup row>
            <Label sm={4} for="op-selfServiceCampaignStatus">
              Self Service Campaign Status
            </Label>
            <Col sm={8}>
              <SingleSelect
                options={selfServiceCampaignStatusesOptions}
                id="op-selfServiceCampaignStatus"
                name="selfServiceCampaignStatus"
                value={selfServiceCampaignStatus}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        )}
        <OpportunityCampaignObjective
          opportunity={model}
          {...inputProps}
          readOnly={readOnly || isSalesforceOpp}
        />
        <FormGroup row>
          <Label sm={4} for="op-useCase" className="required">
            Use Case
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={useCasesOptions}
              id="op-useCase"
              name="useCase"
              value={useCase}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-campaignType" className="required">
            Campaign Type
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={campaignTypeOptions}
              id="op-campaignType"
              name="campaignType"
              value={campaignType}
              {...inputProps}
              readOnly={readOnly || isSalesforceOpp}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-commercialPackageId">
            Commercial Package
          </Label>
          <Col sm={8}>
            <Association
              id="op-commercialPackageId"
              name="commercialPackageId"
              value={commercialPackageId}
              type="commercialPackage"
              {...inputProps}
              readOnly={
                (hasPcLeadRole &&
                  !isSameSalesUnitAsOwner &&
                  isSalesChannelSelfService) ||
                !onChange
              }
            />
          </Col>
        </FormGroup>
      </div>
    </>
  );
}

OpportunityDetails.propTypes = {
  model: opportunityShape.isRequired,
  readOnly: bool,
  onChange: func,
  onSubmit: func,
  embeddedInAgreement: bool,
  isSalesChannelSelfService: bool,
  hasPcLeadRole: bool,
  isSameSalesUnitAsOwner: bool,
  isSalesforceOpp: bool,
};

OpportunityDetails.defaultProps = {
  onChange: undefined,
  readOnly: false,
  onSubmit: undefined,
  embeddedInAgreement: false,
  isSalesChannelSelfService: false,
  hasPcLeadRole: false,
  isSameSalesUnitAsOwner: false,
  isSalesforceOpp: false,
};

export default OpportunityDetails;
